<script lang="ts" setup>
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import {
  EventSource,
  SourceTrack,
  trackItems,
} from '@/models/event-source.model';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { EventsActions, EventsState } from '@/stores/events.store';
import { NEW_ID } from '@/util/constants';
import { computed, onMounted, PropType, ref, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';

const store = useStore<ShiftCMSState & { ev: EventsState }>();

const events = useEvents();

const props = defineProps({
  data: {
    type: Object as PropType<EventSource>,
  },
});

const valid = ref<boolean>(false);

const source = ref<EventSource>({
  id: NEW_ID,
  creationDate: '',
  name: '',
  url: '',
  track: SourceTrack.C,
  lastSuccessfulRun: null,
  lastEventCount: 0,
  consecutiveFailures: 0,
  enabled: false,
});

const action = computed<string>(() =>
  source.value.id === NEW_ID ? 'Add' : 'Save',
);

const saveEventSource = async () => {
  let success: boolean = false;
  if (valid.value) {
    if (source.value.id === NEW_ID) {
      success = await store.dispatch(
        EventsActions.AddEventSource,
        source.value,
      );
    } else {
      success = await store.dispatch(
        EventsActions.UpdateEventSource,
        source.value,
      );
    }
  }
  if (success) {
    events.emit(Event.Saved, true);
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved Event Source',
      type: 'success',
    });
    store.commit(Mutations.HideDrawer);
    store.dispatch(EventsActions.GetEventSources);
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: 'Failed to save Event Source',
      type: 'error',
    });
  }
};

const updateLanguage = async () => {
  if (source.value.id === NEW_ID) {
    Object.assign(source, { title: '' });
  } else {
    const cat = await store.dispatch(
      EventsActions.GetEventSource,
      source.value.id,
    );

    if (cat) {
      Object.assign(source.value, { ...cat });
    }
  }
};

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);

  if (props.data) {
    Object.assign(source.value, {
      ...props.data,
    });
  }
});

const removeLabel = async () => {
  if (
    source.value &&
    source.value.id !== NEW_ID &&
    confirm('Are you absolutely sure you want to delete this "EventSource"?')
  ) {
    store.dispatch(EventsActions.DeleteEventSource, source.value.id);

    store.commit(Mutations.HideDrawer);
  }
};

function checkUrl(url: string) {
  window.open(url, '_new');
}
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveEventSource">
    <v-container>
      <h2>{{ $t(action) }} a event source <CurrentLanguage /></h2>
      <v-row>
        <v-col cols="9">
          <v-text-field
            v-model="source.name"
            label="Title"
            required
          ></v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="9">
          <v-text-field
            v-model="source.url"
            label="URL"
            required
          ></v-text-field>
        </v-col>
        <v-col class="pt-6">
          <v-btn
            block
            color="info"
            @click="checkUrl(source.url)"
            :disabled="source.url == ''"
            >Check</v-btn
          >
        </v-col>
      </v-row>
      <v-row>
        <v-col align="right">
          <v-checkbox label="Enabled" v-model="source.enabled" />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-select
            v-model="source.track"
            label="Processing track"
            class="calculation-field"
            :items="trackItems"
            hide-details="auto"
            :style="{ background: 'white' }"
            required
          ></v-select>
        </v-col>
        <v-col>
          <v-alert color="success" v-if="source.track === SourceTrack.A"
            >Aggregator <strong>APIs</strong> (Luma, Mobilizon, Eventbrite).
            Fully structured, no LLM needed.</v-alert
          >
          <v-alert color="yellow" v-if="source.track === SourceTrack.B"
            ><strong>Custom scrapers</strong> (wijkconnect, buurtgroen020,
            halloijburg). Deterministic HTML scraping per known site
            layout.</v-alert
          >
          <v-alert color="red" v-if="source.track === SourceTrack.C"
            ><strong>LLM extraction</strong>. Any URL — markdown gets fed to
            Haiku, which extracts events.
          </v-alert>
        </v-col>
      </v-row>

      <!-- <CategoriesField v-model="source.categories" /> -->

      <v-row>
        <v-col col="2"></v-col>
        <v-col>
          <v-btn
            class="mt-2"
            type="button"
            color="warning"
            @click="removeLabel"
            block
            >Delete</v-btn
          >
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="reset" block>Reset</v-btn>
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="submit" color="success" block>{{
            action
          }}</v-btn>
        </v-col>
        <v-col col="2"></v-col>
      </v-row>
    </v-container>
  </v-form>
</template>
<style scoped>
pre {
  font-family: sans-serif;
  font-size: 12px;
  background-color: rgb(255, 230, 171);
  display: block;
  height: 100%;
  padding: 8px;
}
</style>
